import { useEffect, useState, cloneElement, ReactElement } from 'react'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useSuggestions } from './useSuggestions'
import { Suggestion, SuggestionReview } from 'api/clients/SuggestionsClient'
import { UserCard } from 'types'
import { SuggestionReviewApprovedRealtimeNotificationModal } from 'components/SuggestionReviewApprovedRealtimeNotificationModal/SuggestionReviewApprovedRealtimeNotificationModal'
import { SuggestionReviewRejectedRealtimeNotificationModal } from 'components/SuggestionReviewRejectedRealtimeNotificationModal/SuggestionReviewRejectedRealtimeNotificationModal'
import { SuggestionsService } from 'api/services/SuggestionsService'
import { useStore } from 'react-redux'
import { LazyComponentWrapper } from 'components/LazyComponentWrapper/LazyComponentWrapper'
import { lazyRetry } from 'utils/lazyRetry'

const SuggestionStatusModal = lazyRetry(() => import('components/SuggestionStatusModal/SuggestionStatusModal'))

enum NotificationAction {
  Approve = 'approve',
  Reject = 'reject',
  Cancel = 'cancel',
}

interface Actor extends UserCard {
  id: number
}

interface BaseNotificationPayload {
  actor: Actor
  suggestion: Suggestion
}

interface SuggestionReviewApprovedNotificationPayload extends BaseNotificationPayload {
  action: NotificationAction.Approve
  review: SuggestionReview
}

interface SuggestionReviewRejectedNotificationPayload extends BaseNotificationPayload {
  action: NotificationAction.Reject
  review: SuggestionReview
}

type NotificationPayload = SuggestionReviewRejectedNotificationPayload | SuggestionReviewApprovedNotificationPayload

const SUGGESTION_WS_CHANNEL_NAME = 'Workflows::SuggestionChannel'

const useSuggestionsRealtimeNotifications = (userId: number | undefined, articleKey: string) => {
  const { outboundSuggestedByMe, outboundSuggestedByOthers } = useSuggestions(userId, articleKey)
  const websocketConnection = useWebsocketConnection()
  const [showModal, setShowModal] = useState(false)
  const [currentModal, setCurrentModal] = useState<ReactElement | null>(null)
  const store = useStore()

  const suggestion = outboundSuggestedByMe || outboundSuggestedByOthers

  useEffect(() => {
    if (suggestion === null || userId === suggestion.suggester.id) {
      return
    }

    const statusModal = showModal ? (
      <LazyComponentWrapper
        component={
          <SuggestionStatusModal
            visible={showModal}
            setVisible={setShowModal}
            suggestion={suggestion}
            onClose={() => SuggestionsService.show(store, suggestion.id)}
          />
        }
      />
    ) : null

    const subscription = websocketConnection?.subscriptions.create(
      {
        channel: SUGGESTION_WS_CHANNEL_NAME,
        id: suggestion.id,
      },
      {
        received: (data: NotificationPayload) => {
          if (data.actor.id === userId) {
            return
          }
          let modalProps = {
            visible: showModal,
            setVisible: setShowModal,
            suggestion: data.suggestion,
            review: data.review,
            onClose: () => setCurrentModal(statusModal),
          }
          SuggestionsService.index(store, articleKey, {})
          switch (data.action) {
            case NotificationAction.Approve:
              setCurrentModal(<SuggestionReviewApprovedRealtimeNotificationModal {...modalProps} />)
              setShowModal(true)
              break
            case NotificationAction.Reject:
              setCurrentModal(<SuggestionReviewRejectedRealtimeNotificationModal {...modalProps} />)
              setShowModal(true)
              break
          }
        },
      },
    )

    return () => {
      subscription?.unsubscribe()
    }
  }, [websocketConnection, suggestion, userId, showModal, store, articleKey])

  return { modal: currentModal && cloneElement(currentModal, { visible: showModal, setVisible: setShowModal }) }
}

export { useSuggestionsRealtimeNotifications }
